/**
 * The Shopify App Store install handoff across a Google OAuth round trip.
 *
 * The OAuth callback sends a merchant to /signup?shopify=connected&shop=…
 * with the store parked server-side for one hour. "Sign up with Google"
 * leaves the page for accounts.google.com and returns to /auth/callback with
 * only a token — the query string, and with it the store, was gone, so a
 * merchant who chose Google ended up with an account and no store. The shop
 * is parked here (sessionStorage: this tab, this browser, cleared on read)
 * before leaving, and taken back by AuthCallback once the session exists.
 */
const KEY = 'bbx_shopify_handoff_shop';
const SHOP_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;

export function rememberShopifyHandoff(shop: string | null | undefined): void {
  try {
    if (shop && SHOP_REGEX.test(shop)) sessionStorage.setItem(KEY, shop);
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the
    // handoff then falls back to the manual connect from Integrations.
  }
}

/** The parked shop, if any — removed as it is read so it can only attach once. */
export function takeShopifyHandoff(): string | null {
  try {
    const shop = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return shop && SHOP_REGEX.test(shop) ? shop : null;
  } catch {
    return null;
  }
}

/**
 * "Connect Shopify" was clicked INSIDE BuzzBit by a signed-in merchant.
 *
 * The App Store round trip (listing → Shopify grant → OAuth callback → back
 * here) ends on the signup page, which since 2026-09-24 asks a signed-in
 * merchant whether the parked store should attach to THIS account — the
 * right question when the install began on the App Store and the browser
 * happens to hold some account, and a pointless one when the merchant
 * pressed Connect in this very product a minute ago. The click is
 * remembered here so the return attaches the store without asking, the way
 * a connector click does: Shopify's own picker and grant are the only
 * screens the merchant sees. Fifteen minutes is longer than any grant takes
 * and shorter than a forgotten tab.
 */
const INTENT_KEY = 'bbx_shopify_connect_intent';
const INTENT_MAX_AGE_MS = 15 * 60 * 1000;

export function rememberShopifyConnectIntent(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(INTENT_KEY, String(now));
  } catch {
    // Without storage the return simply asks, as it would for an App Store start.
  }
}

/** Whether Connect was pressed here recently — removed as it is read so it answers once. */
export function takeShopifyConnectIntent(now: number = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    sessionStorage.removeItem(INTENT_KEY);
    const at = Number(raw);
    return Number.isFinite(at) && at > 0 && now - at >= 0 && now - at <= INTENT_MAX_AGE_MS;
  } catch {
    return false;
  }
}
